import { BaseService } from "../BaseService";
import {
  CreateDashboardRequest,
  Dashboard,
  UpdateDashboardRequest,
  WidgetDataMap,
} from "@/types/breeze/Dashboard";

export class DashboardService extends BaseService {
  public async list(): Promise<Dashboard[]> {
    return this.getRequest<Dashboard[]>("/dashboards");
  }

  public async getMine(): Promise<Dashboard> {
    return this.getRequest<Dashboard>("/dashboards/me");
  }

  public async getById(id: number): Promise<Dashboard> {
    return this.getRequest<Dashboard>(`/dashboards/${id}`);
  }

  public async create(request: CreateDashboardRequest): Promise<Dashboard> {
    return this.postRequest<CreateDashboardRequest, Dashboard>(
      "/dashboards",
      request,
    );
  }

  public async update(
    id: number,
    request: UpdateDashboardRequest,
  ): Promise<Dashboard> {
    // Caller controls the toast — the dashboard page tailors it per action
    // (save / share / set-home), so suppress the default "Submitted successfully".
    return this.putRequest<UpdateDashboardRequest, Dashboard>(
      `/dashboards/${id}`,
      request,
      undefined,
      true,
      true,
    );
  }

  public async remove(id: number): Promise<void> {
    await this.deleteRequest(`/dashboards/${id}`);
  }

  public async getData(id: number, widgetIds: number[] = []): Promise<WidgetDataMap> {
    // POST-shaped read; suppress the default "Submitted successfully" toast.
    return this.postRequest<{ widget_ids: number[] }, WidgetDataMap>(
      `/dashboards/${id}/data`,
      { widget_ids: widgetIds },
      undefined,
      true,
      true,
    );
  }

  public async previewWidget(type: string, configJson: string | null): Promise<unknown> {
    // POST-shaped read; suppress the default toast.
    return this.postRequest<{ type: string; config_json: string | null }, unknown>(
      "/dashboards/widgets/preview",
      { type, config_json: configJson },
      undefined,
      true,
      true,
    );
  }

  public async promoteToDefault(id: number): Promise<Dashboard> {
    return this.postRequest<Record<string, never>, Dashboard>(
      `/dashboards/${id}/promote-to-default`,
      {},
    );
  }

  public async setHome(id: number): Promise<Dashboard> {
    return this.postRequest<Record<string, never>, Dashboard>(
      `/dashboards/${id}/set-home`,
      {},
    );
  }
}

export const dashboardService = new DashboardService();
